import { Button, ButtonType, DownloadDropdown, FilterBar } from "../../Components";
import { ButtonFrameworkVariant } from "../../Components/ButtonFramework";
import { CrossChannelContext } from "../CrossChannel";
import { DateRange, StateSetter } from "../../utils/types";
import { download } from "../../utils/download-utils";
import { GLOSSARY, METRIC_TO_PRETTY_NAME } from "../crossChannelConstants";
import { GlossaryModal } from "../../Components/GlossaryModal/GlossaryModal";
import { KpiInfo } from "@blisspointmedia/bpm-types/dist/Kpis";
import { LOCATIONS } from "../../NavBar/locations";
import { MdViewHeadline } from "react-icons/md";
import { Metric } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import { ReactComponent as ColorScale } from "../../SingleChannel/colorScale.svg";
import { useCompanyInfo } from "../../redux/company";
import { useMap } from "../../utils/hooks/useData";
import { useSelector } from "react-redux";
import { useSetError } from "../../redux/modals";
import * as CC from "@blisspointmedia/bpm-types/dist/CrossChannelPerformance";
import * as R from "ramda";
import * as UserRedux from "../../redux/user";
import * as uuid from "uuid";
import EditTablePresetModal from "../../SingleChannel/MetricsTableConfig/EditTablePresetModal";
import MetricsTableWithPresets from "../../SingleChannel/MetricsTable/MetricsTableWithPresets";
import PerformanceGridSkeleton from "../../Performance/PerformanceGridSkeleton";
import PresetDropdown from "../../SingleChannel/PresetDropdown";
import RenameModal from "../../SingleChannel/RenameModal";
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  SingleChannelLambdaFetch,
} from "../../utils/fetch-utils";
import {
  Column,
  DeletePresetParams as DeleteTablePresetParams,
  DimensionColumn,
  GetPresetParams as GetTablePresetParams,
  MetricsTablePreset,
  PresetIDGroups as SimpleMetricsTablePreset,
  SavePresetParams as SaveTablePresetParams,
  SavePresetResponse as SaveTablePresetResponse,
  SuperHeader,
} from "@blisspointmedia/bpm-types/dist/MetricsTable";
import {
  DimensionMap,
  CellData,
  ColumnMetaDataMap,
  constructTableData,
  DEFAULT_GET_DIMENSION_CELL,
  getDataHeaders,
  getDimensionDataHeaders,
  sortData,
  getExportedData,
  joinMetricsTableData,
} from "../../SingleChannel/MetricsTable/metricsTableUtils";
import {
  GetPageDataResponse,
  PerformanceDataRow,
} from "@blisspointmedia/bpm-types/dist/Performance";
import {
  HeatMappingDivergent0,
  HeatMappingDivergentNeg1,
  HeatMappingDivergentNeg2,
  HeatMappingDivergentNeg3,
  HeatMappingDivergentPos1,
  HeatMappingDivergentPos2,
  HeatMappingDivergentPos3,
} from "../../utils/colors";

interface MetricsTableViewProps {
  columnMetaDataMap: ColumnMetaDataMap;
  dates: DateRange;
  dimensionColumnMetaDataMap: ColumnMetaDataMap;
  getDimensionCell?: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: any[]
  ) => CellData;
  kpi: string;
  kpiMetaData: Record<string, KpiInfo>;
  metricsTableFetchFilterBar: JSX.Element | undefined;
  prefix: string;
  refreshCustomMetrics: () => void;
  setMetricsTableFetchFilterBar: StateSetter<JSX.Element | undefined>;
}

const metricsPreset: Partial<MetricsTablePreset> = {
  dimensionColumns: [
    {
      dimensionTypeName: "Channel",
      dimensionVarName: "Channel",
      divider: false,
      id: uuid.v4(),
      label: "Channel",
      role: 10,
    },
    {
      dimensionTypeName: "Platform",
      dimensionVarName: "Platform",
      divider: false,
      id: uuid.v4(),
      label: "Platform",
      role: 10,
    },
  ],
  dataColumns: [
    {
      dataVarName: Metric.CLICKS,
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.CLICKS],
      role: 10,
    },
    {
      dataVarName: Metric.SPEND,
      id: uuid.v4(),
      role: 10,
    },
    {
      dataVarName: Metric.IMPRESSIONS,
      id: uuid.v4(),
      role: 10,
    },
    {
      dataVarName: Metric.VOLUME,
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.VOLUME],
      role: 10,
    },
    {
      dataVarName: Metric.REVENUE,
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.REVENUE],
      role: 10,
    },
    {
      dataVarName: Metric.CPM,
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.CPM],
      role: 10,
    },
    {
      dataVarName: Metric.ROAS,
      heatMapping: { colorScheme: "sequential" },
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.ROAS],
      role: 10,
    },
    {
      dataVarName: Metric.CPX,
      heatMapping: { colorScheme: "sequential" },
      id: uuid.v4(),
      label: METRIC_TO_PRETTY_NAME[Metric.CPX],
      role: 10,
    },
  ],
};

const Metrics: React.FC<MetricsTableViewProps> = ({
  columnMetaDataMap,
  dates,
  dimensionColumnMetaDataMap,
  getDimensionCell,
  kpi,
  kpiMetaData,
  prefix,
  refreshCustomMetrics,
}) => {
  const {
    company,
    fetchingTablePresets,
    filterID,
    filterSource,
    filterState,
    getTablePresets,
    metricsTableDataKey,
    metricsTableDataOtherKey,
    metricsTableDataMap,
    pagePreset,
    setFetchingTablePresets,
    setMetricsTableDataMap,
    setURL,
    tablePresetName,
    tablePresets,
    selectedSources,
    otherDates,
  } = useContext(CrossChannelContext);
  const [filter, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();
  const [rerenderTable, setRerenderTable] = useState(false);
  const [showEditTablePresetModal, setShowEditTablePresetModal] = useState<boolean>(false);
  const [showNewTablePresetModal, setShowNewTablePresetModal] = useState<boolean>(false);
  const [showRenameTablePresetModal, setShowRenameTablePresetModal] = useState<boolean>(false);
  const [metricTableDataLoading, setMetricsTableDataLoading] = useState<boolean>(false);
  const [metricTableOtherDataLoading, setMetricsTableOtherDataLoading] = useState<boolean>(false);
  const [tableHeight, setTableHeight] = useState(250);
  const [tablePresetChanges, setTablePresetChanges] = useState<MetricsTablePreset>(
    {} as MetricsTablePreset
  );
  const [tablePresetMap, setTablePresetMap] = useMap<string, MetricsTablePreset | undefined>();
  const [tableWidth, setTableWidth] = useState(500);
  const companyInfo = useCompanyInfo(company);
  const containerRef = useRef(null);
  const defaultTablePresetNames = R.keys(dimensionColumnMetaDataMap);
  const isExperimental =
    process.env.REACT_APP_DOCKER_LAMBDA_HOST === "https://api-staging-2.blisspointmedia.com" ||
    window.location.hostname === "localhost";
  const isAdmin = useSelector(UserRedux.isAdminSelector);
  const setError = useSetError();
  const tablePreset = tablePresetMap[`${tablePresetName}`];
  const [exportedData, setExportedData] = useState<(string | number)[][]>([]);
  const [showComparison, setShowComparison] = useState<boolean>(false);

  const tablePresetID: SimpleMetricsTablePreset | undefined = useMemo(
    () =>
      R.find(
        preset => preset.name === tablePresetName,
        R.defaultTo([], tablePresets) as SimpleMetricsTablePreset[]
      ),
    [tablePresetName, tablePresets]
  );

  const data = useMemo(
    () =>
      metricsTableDataMap[metricsTableDataKey] as GetPageDataResponse<
        PerformanceDataRow<CC.Dimension, CC.ColumnDataRow>
      >,
    [metricsTableDataKey, metricsTableDataMap]
  );

  const otherData = useMemo(
    () =>
      metricsTableDataMap[metricsTableDataOtherKey] as GetPageDataResponse<
        PerformanceDataRow<CC.Dimension, CC.ColumnDataRow>
      >,
    [metricsTableDataOtherKey, metricsTableDataMap]
  );

  // Table Preset Callbacks
  const getTablePreset = useCallback(
    params => {
      return (async () => {
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<GetTablePresetParams>(
            "/metrics_table_preset",
            {
              params,
            }
          );
          return await awaitJSON<MetricsTablePreset>(res);
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to fetch table preset. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          setFetchingTablePresets(false);
        }
      })();
    },
    [setError, setFetchingTablePresets]
  );

  const saveTablePreset = useCallback(
    body => {
      return (async () => {
        let presetResponse;
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<SaveTablePresetParams>(
            "/metrics_table_preset",
            {
              method: "POST",
              body,
            }
          );
          presetResponse = await awaitJSON<SaveTablePresetResponse>(res);
          return presetResponse;
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to save table preset. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          getTablePresets();
        }
      })();
    },
    [getTablePresets, setError, setFetchingTablePresets]
  );

  const duplicateTablePreset = useCallback(
    preset => {
      (async () => {
        const fullyLoadedPreset =
          preset && !R.isNil(preset.name) && tablePresetMap[`${preset.name}`]
            ? tablePresetMap[`${preset.name}`]
            : await getTablePreset({ id: preset.id, company, mediatype: prefix });
        if (!tablePresetMap[`${preset.name}`] && fullyLoadedPreset) {
          setTablePresetMap(`${preset.name}`, fullyLoadedPreset);
        }
        saveTablePreset({
          company,
          id: "new",
          mediatype: prefix,
          preset: fullyLoadedPreset,
          name: `${preset.name} (Copy)`,
        });
      })();
    },
    [company, getTablePreset, prefix, saveTablePreset, setTablePresetMap, tablePresetMap]
  );

  const deleteTablePreset = useCallback(
    params => {
      return (async () => {
        try {
          setFetchingTablePresets(true);
          const res = await SingleChannelLambdaFetch<DeleteTablePresetParams>(
            "/metrics_table_preset",
            {
              method: "DELETE",
              params,
            }
          );
          await awaitJSON(res);
        } catch (e) {
          const error = e as Error;
          setError({
            message: `Failed to delete table preset. Error: ${error.message}`,
            reportError: error,
          });
        } finally {
          getTablePresets();
        }
      })();
    },
    [getTablePresets, setError, setFetchingTablePresets]
  );

  // Metrics Table Data Fetch
  const fetchMetricsTableData = useCallback(
    async ({ dates, byDate = false }) => {
      try {
        if (tablePresetID && pagePreset) {
          const dateRangeToUse = dates;
          const params: any = {
            ...LOCATIONS["Cross-Channel"].subPages.Overview.testCompanyParams,
            ...(dateRangeToUse || {}),
            pageID: R.defaultTo({ id: 0 }, pagePreset).id,
            company,
            tableID: tablePresetID.id,
            id: 0, // TODO: remove this
            kpi,
            sources: selectedSources.join(","),
          };
          if (isExperimental) {
            params.lag = companyInfo.streaming_performance_default_lag;
          }
          // We don't want to check for the filter state if we don't have a preset and
          // if we haven't loaded the filter state from the page/filter preset
          if (filterSource === "currentState" && !R.isNil(filterState)) {
            params.filterState = JSON.stringify(filterState);
          } else if (filterSource === "currentFilterID" && !R.isNil(filterID)) {
            params.filterID = filterID;
          } else if (
            pagePreset &&
            filterSource === "presetFilterID" &&
            !R.isNil(pagePreset.filterInfo.filterID)
          ) {
            params.filterID = pagePreset.filterInfo.filterID;
          } else if (
            pagePreset &&
            filterSource === "presetState" &&
            pagePreset.filterInfo.filterState
          ) {
            params.filterState = JSON.stringify(pagePreset.filterInfo.filterState);
          }

          if (byDate) {
            params.byDate = "true";
          }

          const res = await CrossChannelLambdaFetch("/getCrossChannelMetricsTableData", {
            params,
          });
          const data = await awaitJSON(res);
          return data;
        }
      } catch (e) {
        const reportError = e as Error;
        setError({
          reportError,
          message: reportError.message,
        });
      }
    },
    [
      company,
      companyInfo.streaming_performance_default_lag,
      filterID,
      filterSource,
      filterState,
      isExperimental,
      kpi,
      pagePreset,
      selectedSources,
      setError,
      tablePresetID,
    ]
  );

  // Metrics Table Data Fetch and Set
  const fetchAndSetMetricsTableData = useCallback(
    (key, dates) => {
      (async () => {
        if (tablePresetID) {
          try {
            const data = await fetchMetricsTableData({ dates });
            setMetricsTableDataMap(map => ({ ...map, [key]: data }));
          } catch (e) {
            const error = e as Error;
            setError({
              reportError: error,
              message: error.message,
            });
          }
        }
        setMetricsTableDataLoading(false);
      })();
    },
    [tablePresetID, fetchMetricsTableData, setMetricsTableDataMap, setError]
  );

  const dataFetchKey = useCallback(
    dataHeader =>
      `${dataHeader && dataHeader.kpi ? dataHeader.kpi : kpi || undefined}_${
        companyInfo.streaming_performance_default_lag
      }`,
    [companyInfo.streaming_performance_default_lag, kpi]
  );

  // This allows exporting table data by day. Needs to refetch data with byDate set to true,
  // and then do similar processing of exportedData that the Metrics Table handles.
  const exportTableDataByDay = useCallback(async () => {
    const data = await fetchMetricsTableData({ dates, byDate: true });
    const role = 10;
    const preset = R.defaultTo(metricsPreset, tablePreset) as MetricsTablePreset;
    const dataHeaders = getDataHeaders(preset, role);
    const dimensionDataHeaders = getDimensionDataHeaders(preset, role);
    // Need to manually add Date column to dimensionDataHeaders
    dimensionDataHeaders.push({
      id: "",
      role: 10,
      // @ts-ignore
      dimensionVarName: "Date",
      // @ts-ignore
      dimensionTypeName: "Date",
    });

    const { dimensionData, tableData } = constructTableData(
      columnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      getDimensionCell || DEFAULT_GET_DIMENSION_CELL,
      dataFetchKey,
      data.data
    );

    const sorting = preset.defaultSorting || [];

    const [sortedDimensionData, sortedTableData] = sortData(
      dataHeaders,
      dimensionData,
      dimensionDataHeaders,
      sorting,
      tableData,
      true
    );

    const numDimCols = dimensionDataHeaders.length;
    const numDataCols = dataHeaders.length;

    const dataToExport = getExportedData({
      columnMetaDataMap,
      dataHeaders,
      dimensionDataHeaders,
      numDataCols,
      numDimCols,
      sortedDimensionData,
      sortedTableData,
    });

    const csvContent = R.map(
      e =>
        e
          .map(cell =>
            typeof cell === "number"
              ? cell
              : typeof cell === "string"
              ? cell.replace(/,/g, "")
              : cell
          )
          .join(","),
      dataToExport
    ).join("\n");
    download(csvContent, `Metrics_Table_Kpi_${kpi}_byDay.csv`, "text/csv");
  }, [
    fetchMetricsTableData,
    dates,
    tablePreset,
    columnMetaDataMap,
    getDimensionCell,
    kpi,
    dataFetchKey,
  ]);

  const exportTableData = useCallback(() => {
    const csvContent = R.map(
      e =>
        e
          .map(cell => (typeof cell === "number" ? cell : R.defaultTo("-", cell).replace(/,/g, "")))
          .join(","),
      exportedData
    ).join("\n");
    download(csvContent, `Metrics_Table_Kpi_${kpi}.csv`, "text/csv");
  }, [exportedData, kpi]);

  // Metrics Table Controls
  const tableFilterBar = (
    <div className="filterBarContainer">
      <FilterBar
        hasAdvanced={false}
        onFilter={filter => {
          setFilter({ filter });
        }}
        options={
          metricsPreset && metricsPreset.dimensionColumns
            ? R.map(col => col.dimensionVarName, metricsPreset.dimensionColumns)
            : []
        }
        size="lg"
        width={770}
      />
    </div>
  );

  const tablePresetPicker = (
    <PresetDropdown
      addPresetOnClick={() => {
        setTablePresetChanges(
          changes => ({ ...changes, id: "new", name: "My New Table Preset" } as any)
        );
        setShowNewTablePresetModal(true);
      }}
      addPresetText={"Add New Table Preset"}
      applyOnClick={tablePreset => {
        if (pagePreset) {
          setURL(pagePreset.name, tablePreset.name);
        }
      }}
      cancelOnClick={() => {}}
      className={`tablePresetPicker ${
        fetchingTablePresets || R.isNil(tablePresets) ? "loading" : ""
      }`}
      defaultPresetNames={defaultTablePresetNames}
      deleteItemOnClick={presetID => deleteTablePreset({ id: presetID, mediatype: prefix })}
      duplicateItemOnClick={preset => duplicateTablePreset(preset)}
      editItemOnClick={preset => {
        (async () => {
          const fetchedTablePreset = (tablePresetMap[`${preset.name}`]
            ? tablePresetMap[`${preset.name}`]
            : await getTablePreset({
                id: preset.id,
                company,
                mediatype: prefix,
              })) as MetricsTablePreset;
          if (!R.isNil(tablePresetMap[`${preset.name}`])) {
            setTablePresetMap(`${preset.name}`, fetchedTablePreset);
          }
          setTablePresetChanges(fetchedTablePreset);
          setShowEditTablePresetModal(true);
        })();
      }}
      hasTicks={true}
      label={
        tablePreset && !R.isNil(tablePreset.id)
          ? `Table Preset: ${tablePreset.temporary ? "Temp" : tablePreset.name}`
          : "Load Table Preset"
      }
      presets={R.filter(
        preset => !preset.temporary || (!R.isNil(tablePreset) && preset.id === tablePreset.id),
        R.defaultTo([], tablePresets)
      )}
      renameItemOnClick={(preset: SimpleMetricsTablePreset) => {
        // For table presets, the arg provided here is the minimal version
        setTablePresetChanges({ preset: preset.admin_lock, id: preset.id } as any);
        setShowRenameTablePresetModal(true);
      }}
      selectedPreset={tablePreset}
    />
  );

  const [showColorScale, setShowColorScale] = useState<boolean>(false);
  const colorScaleItems: { label: string; style }[] = [
    { label: "", style: { backgroundColor: HeatMappingDivergentNeg3 } },
    { label: "", style: { backgroundColor: HeatMappingDivergentNeg2 } },
    { label: "", style: { backgroundColor: HeatMappingDivergentNeg1 } },
    {
      label: "Avg",
      style: { backgroundColor: HeatMappingDivergent0, outline: "1px solid #BEEB80" },
    },
    { label: "", style: { backgroundColor: HeatMappingDivergentPos1 } },
    { label: "", style: { backgroundColor: HeatMappingDivergentPos2 } },
    { label: "", style: { backgroundColor: HeatMappingDivergentPos3 } },
  ];
  const colorScale = showColorScale ? (
    <Button
      className="colorScaleButton"
      design="secondary"
      onClick={() => setShowColorScale(false)}
      size={"lg"}
      type={ButtonType.OUTLINED}
      variant={ButtonFrameworkVariant.NO_ICON}
    >
      <div className="colorScale">
        {R.map(
          item => (
            <div style={item.style}>{item.label}</div>
          ),
          colorScaleItems
        )}
      </div>
    </Button>
  ) : (
    <Button
      className="showColorScaleButton"
      design="secondary"
      icon={<ColorScale />}
      onClick={() => setShowColorScale(true)}
      size={"lg"}
      type={ButtonType.OUTLINED}
      variant={ButtonFrameworkVariant.LEADING_ICON}
    >
      See Color Scale
    </Button>
  );
  const [showGlossary, setShowGlossary] = useState<boolean>(false);
  const glossaryButton = (
    <Button
      className="glossaryButton"
      design="secondary"
      icon={<MdViewHeadline />}
      onClick={() => setShowGlossary(true)}
      size={"lg"}
      type={ButtonType.FILLED}
      variant={ButtonFrameworkVariant.LEADING_ICON}
    >
      Glossary
    </Button>
  );

  const downloadButton = (
    <DownloadDropdown
      menuOptions={["Download table data", "Download table data by date"]}
      onClickOptions={[exportTableData, exportTableDataByDay]}
    />
  );

  const showComparisonButton = (
    <Button
      type={ButtonType.FILLED}
      design="secondary"
      className="showComparisonButton"
      onClick={() => {
        setShowComparison(!showComparison);
        setExportedData([]);
      }}
    >
      {showComparison ? "Hide Comparison" : "Show Comparison"}
    </Button>
  );

  const metricsTableControls = (
    <div className="metricsTableControlsContainer">
      {tableFilterBar}
      <div className="metricsTableControls">
        {showComparisonButton}
        {tablePresetPicker}
        {colorScale}
        {glossaryButton}
        {downloadButton}
      </div>
    </div>
  );

  const processedTablePreset: MetricsTablePreset = useMemo(() => {
    const dimensionColumns: DimensionColumn[] = [];
    const dataColumns: Column[] = [];
    if (pagePreset && tablePreset && columnMetaDataMap) {
      R.map(oldDimCol => {
        const columnMetaData =
          oldDimCol && dimensionColumnMetaDataMap[oldDimCol.dimensionTypeName]
            ? dimensionColumnMetaDataMap[oldDimCol.dimensionTypeName]
            : dimensionColumnMetaDataMap[R.keys(dimensionColumnMetaDataMap)[0]];
        if (oldDimCol && R.isNil(dimensionColumnMetaDataMap[oldDimCol.dimensionTypeName])) {
          console.error(
            `${oldDimCol.dimensionTypeName} is not present in the dimension column metadata map`
          );
        }
        const newDimCol: DimensionColumn = {
          ...oldDimCol,
          dimensionVarName: columnMetaData.dimensionVarName,
          dimensionTypeName:
            oldDimCol && dimensionColumnMetaDataMap[oldDimCol.dimensionTypeName]
              ? oldDimCol.dimensionTypeName
              : R.keys(dimensionColumnMetaDataMap)[0],
          label: oldDimCol && oldDimCol.label ? oldDimCol.label : columnMetaData.displayName,
          role: oldDimCol.role ? oldDimCol.role : 10,
        };
        dimensionColumns.push(newDimCol);
        return oldDimCol;
      }, tablePreset.dimensionColumns);
      R.map(oldDataCol => {
        const columnMetaData =
          oldDataCol && columnMetaDataMap[oldDataCol.dataVarName]
            ? columnMetaDataMap[oldDataCol.dataVarName]
            : columnMetaDataMap[R.keys(columnMetaDataMap)[0]];
        if (oldDataCol && R.isNil(columnMetaDataMap[oldDataCol.dataVarName])) {
          console.error(`${oldDataCol.dataVarName} is not present in the column metadata map`);
        }
        const newDataCol: Column = {
          ...oldDataCol,
          dataVarName:
            oldDataCol && columnMetaDataMap[oldDataCol.dataVarName]
              ? oldDataCol.dataVarName
              : R.keys(columnMetaDataMap)[0],
          label: oldDataCol.label ? oldDataCol.label : columnMetaData.displayName,
          role: oldDataCol.role ? oldDataCol.role : 10,
        };
        dataColumns.push(newDataCol);
        return oldDataCol;
      }, tablePreset.dataColumns);
      const processedTablePreset = {
        ...tablePreset,
        dataColumns,
        dimensionColumns,
        globalOptions: {
          ...pagePreset.globalOptions,
        },
      } as any;
      return processedTablePreset;
    }
  }, [columnMetaDataMap, dimensionColumnMetaDataMap, pagePreset, tablePreset]);

  const presetToUse = useMemo(() => {
    const result = R.defaultTo(metricsPreset, processedTablePreset);
    if (showComparison) {
      const compPreset = { ...result };
      const newSuperHeaders: SuperHeader[] = compPreset.dataColumns
        ? compPreset.dataColumns.map(col => {
            return {
              data: {
                text: col.label ? col.label : col.dataVarName,
                leftDivider: true,
                rightDivider: true,
              },
              span: 3,
            };
          })
        : [];
      const newDataCols = compPreset.dataColumns
        ? compPreset.dataColumns.reduce((newCols, col) => {
            newCols.push(
              { ...col, label: "primary" },
              { ...col, dataVarName: `${col.dataVarName}_other`, label: "comparison" },
              { ...col, dataVarName: `${col.dataVarName}_delta`, label: "%∆", decimals: 0 }
            );
            return newCols;
          }, [] as Column[])
        : [];
      compPreset.dataColumns = newDataCols;
      compPreset.superHeaders = newSuperHeaders;
      return compPreset;
    }
    return result;
  }, [processedTablePreset, showComparison]);

  const joinedData = useMemo(
    () =>
      data && data.data && otherData && otherData.data
        ? joinMetricsTableData(data.data, otherData.data)
        : undefined,
    [data, otherData]
  );
  // Metrics Table
  const metricsTable =
    data &&
    !fetchingTablePresets &&
    (showComparison ? (
      joinedData && (
        <MetricsTableWithPresets
          columnMetaDataMap={{ ...columnMetaDataMap, ...dimensionColumnMetaDataMap }}
          data={R.defaultTo([], joinedData) as any}
          dataFetchKey={dataFetchKey}
          filter={filter ? filter.filter : undefined}
          getDimensionCell={getDimensionCell}
          height={tableHeight}
          preset={presetToUse as MetricsTablePreset}
          setExportedData={exportedData => {
            setExportedData(exportedData);
          }}
          width={tableWidth}
          exportedData={exportedData}
        />
      )
    ) : (
      <MetricsTableWithPresets
        columnMetaDataMap={{ ...columnMetaDataMap, ...dimensionColumnMetaDataMap }}
        data={R.defaultTo([], data.data) as any}
        dataFetchKey={dataFetchKey}
        filter={filter ? filter.filter : undefined}
        getDimensionCell={getDimensionCell}
        height={tableHeight}
        preset={R.defaultTo(metricsPreset, processedTablePreset) as MetricsTablePreset}
        setExportedData={exportedData => {
          setExportedData(exportedData);
        }}
        width={tableWidth}
        exportedData={exportedData}
      />
    ));

  // Table Container
  const metricsTableBody =
    data && data.data && tablePreset ? metricsTable : <PerformanceGridSkeleton />;

  // Modals
  const glossaryModal = (
    <GlossaryModal
      show={showGlossary}
      setShow={() => setShowGlossary(false)}
      title="Metrics Glossary"
      initialGlossary={GLOSSARY}
    />
  );

  const renameTablePresetModal = showRenameTablePresetModal && (
    <RenameModal
      applyOnClick={newName => {
        (async () => {
          await saveTablePreset({
            company: company,
            id: tablePresetChanges.id,
            mediatype: prefix,
            name: newName,
            temporary: R.defaultTo(false, tablePresetChanges.temporary),
          });
          const presetInMap = tablePresetMap[`${tablePresetChanges.name}`];
          if (presetInMap) {
            setTablePresetMap(`${tablePresetChanges.name}`, {
              ...presetInMap,
              name: newName,
            });
          }
          if (tablePresetID && tablePresetChanges.id === tablePresetID.id) {
            setTablePresetChanges({ ...tablePresetChanges, name: newName });
          }
          setShowRenameTablePresetModal(false);
          if (pagePreset) {
            setURL(pagePreset.name, newName);
          }
        })();
      }}
      applyText={"Save"}
      disabled={tablePresetChanges.admin_lock && !isAdmin}
      cancelText={"Discard"}
      onCancel={() => {
        setShowRenameTablePresetModal(false);
        if (tablePreset) {
          setTablePresetChanges(tablePreset);
        }
      }}
      onHide={() => {
        setShowRenameTablePresetModal(false);
        if (tablePreset) {
          setTablePresetChanges(tablePreset);
        }
      }}
      placeholder="Enter new preset name"
      subTitle={"New Table Preset Name"}
      title={`Rename ${
        tablePresetChanges && tablePresetChanges.name ? tablePresetChanges.name : ""
      }`}
    />
  );

  const editTablePresetModal = (showEditTablePresetModal || showNewTablePresetModal) &&
    tablePreset && (
      <EditTablePresetModal
        columnMetaDataMap={columnMetaDataMap}
        defaultPresetNames={defaultTablePresetNames}
        dimensionColumnMetaDataMap={R.omit(
          ["Campaign Name", "Campaign ID"],
          dimensionColumnMetaDataMap
        )}
        kpiMetaData={kpiMetaData}
        prefix={prefix as "cross_channel"}
        onHide={() => {
          setShowEditTablePresetModal(false);
          setShowNewTablePresetModal(false);
          if (tablePreset) {
            setTablePresetChanges(tablePreset);
          }
        }}
        presetChanges={tablePresetChanges}
        refreshCustomMetrics={refreshCustomMetrics}
        setPresetChanges={setTablePresetChanges}
        saveAndApplyOnClick={tablePreset => {
          (async () => {
            const newID = await saveTablePreset({
              company,
              companyToUseInDB: company,
              id:
                tablePreset && !R.isNil(tablePreset.id) && tablePreset.id > 0
                  ? tablePreset.id
                  : "new",
              name: tablePreset.name,
              mediatype: prefix,
              preset: tablePreset,
              temporary: tablePreset.temporary,
            });
            if (
              pagePreset &&
              ((tablePreset.id as any) === "new" ||
                tablePreset.id === tablePresetID?.id ||
                tablePreset.temporary)
            ) {
              const tablePresetName =
                tablePreset.temporary &&
                (R.isNil(tablePreset.id) ||
                  (tablePreset.id as any) === "new" ||
                  tablePreset.id <= 0)
                  ? newID.name
                  : tablePreset.name;
              setURL(pagePreset.name, tablePresetName);
              setMetricsTableDataMap(map => ({
                ...map,
                [metricsTableDataKey]: undefined,
                [metricsTableDataOtherKey]: undefined,
              }));
            }
            setTablePresetMap(`${newID.name}`, {
              ...tablePresetChanges,
              name: (newID || {}).name,
              id: (newID || {}).id,
              temporary: tablePreset.temporary,
            });
            setShowEditTablePresetModal(false);
            setShowNewTablePresetModal(false);
          })();
        }}
      />
    );

  // useEffects
  useEffect(() => {
    if (company && !tablePresets) {
      getTablePresets();
    }
  }, [company, getTablePresets, tablePresets]);

  useEffect(() => {
    (async () => {
      if (R.isNil(metricsTableDataMap[metricsTableDataKey]) && !metricTableDataLoading) {
        setMetricsTableDataLoading(true);
        await fetchAndSetMetricsTableData(metricsTableDataKey, dates);
      }
    })();
  }, [
    dates,
    fetchAndSetMetricsTableData,
    kpi,
    metricsTableDataKey,
    metricsTableDataMap,
    metricTableDataLoading,
  ]);

  useEffect(() => {
    (async () => {
      if (R.isNil(metricsTableDataMap[metricsTableDataOtherKey]) && !metricTableOtherDataLoading) {
        if (tablePresetID) {
          try {
            setMetricsTableOtherDataLoading(true);
            const data = await fetchMetricsTableData({ dates: otherDates });
            setMetricsTableDataMap(map => ({ ...map, [metricsTableDataOtherKey]: data }));
          } catch (e) {
            const error = e as Error;
            setError({
              reportError: error,
              message: error.message,
            });
          }
        }
        setMetricsTableOtherDataLoading(false);
      }
    })();
  }, [
    otherDates,
    kpi,
    metricsTableDataOtherKey,
    metricsTableDataMap,
    metricTableOtherDataLoading,
    fetchMetricsTableData,
    setError,
    setMetricsTableDataMap,
    tablePresetID,
  ]);

  useEffect(() => {
    if (!R.isNil(tablePresetName) && !tablePreset && tablePresetMap && !fetchingTablePresets) {
      (async () => {
        if (tablePresetID) {
          try {
            const tablePreset = await getTablePreset({
              id: `${tablePresetID.id}`,
              company,
              mediatype: prefix,
            });
            if (pagePreset && tablePreset) {
              setTablePresetMap(`${tablePresetID.name}`, tablePreset);
              setTablePresetChanges(tablePreset);
              setURL(pagePreset.name, tablePreset.name);
            }
          } catch (e) {
            const error = e as Error;
            setError({
              reportError: error,
              message: error.message,
            });
          }
        }
      })();
    }
  }, [
    tablePreset,
    tablePresetID,
    setTablePresetMap,
    tablePresetMap,
    company,
    prefix,
    setError,
    getTablePreset,
    tablePresetName,
    tablePresets,
    setURL,
    pagePreset,
    fetchingTablePresets,
  ]);

  // Component Sizing
  const windowResizeFunction = () => setRerenderTable(true);
  if (window && window.onresize !== windowResizeFunction) {
    window.onresize = windowResizeFunction;
  }

  useLayoutEffect(() => {
    const newTableHeight = containerRef.current
      ? (containerRef.current as any).offsetHeight
      : undefined;
    const newTableWidth = containerRef.current
      ? (containerRef.current as any).offsetWidth
      : undefined;

    if (newTableHeight) {
      setTableHeight((containerRef.current as any).offsetHeight);
    }
    if (newTableWidth) {
      setTableWidth((containerRef.current as any).offsetWidth);
    }

    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  useLayoutEffect(() => {
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  return (
    <div
      className="metricsTableWithControlsContainer"
      style={{ display: "flex", flexDirection: "column", flex: 1 }}
    >
      {editTablePresetModal}
      {renameTablePresetModal}
      {glossaryModal}
      {metricsTableControls}
      <div id="metricsTableContainer" ref={containerRef}>
        {metricsTableBody}
      </div>
    </div>
  );
};

export default Metrics;
